import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import useReveal from "@/hooks/useReveal";

const ACCENT = "#004D40"; // teal

const VULNERABILITIES = [
  "Being overcharged for a service",
  "Receiving and trusting poor advice",
  "Buying products that were never needed",
  "Making avoidable financial mistakes",
  "Depending unnecessarily on middlemen",
  "Choosing the wrong path with incomplete information",
  "Falling for manipulation or misinformation",
  "Missing valuable opportunities entirely",
];

const QUESTIONS = [
  "How do I know whether I am being overcharged?",
  "How do I verify information before I trust it?",
  "How do I judge whether advice is sound?",
  "How do I understand the systems I have to deal with?",
  "How do I make a financial decision with confidence?",
  "How do I protect myself from a costly mistake?",
  "How do I recognise a real opportunity?",
  "How do I become genuinely independent?",
];

const LEVELS = [
  {
    href: "/level-1",
    accent: "#004D40",
    badge: "Level 1 — Home Intelligence",
    body: "We close the gap between a child and their immediate environment — understanding, managing, and responding to situations inside the home and personal life.",
  },
  {
    href: "/level-2",
    accent: "#004D40",
    badge: "Level 2 — System Intelligence",
    body: "We close the gap between a student and the systems that shape society — how banking, government services, identity systems, digital platforms, and public services actually work.",
  },
  {
    href: "/level-3",
    accent: "#F57F17",
    badge: "Level 3 — Life Intelligence",
    body: "We close the gap between a young adult and life's major decisions — how money works, how businesses operate, how careers are chosen, how risks are managed, and how responsible decisions are made.",
  },
];

export default function Philosophy() {
  useReveal();
  useEffect(() => { document.title = "Philosophy — LifeIQ"; }, []);

  return (
    <main>
      {/* HERO */}
      <section
        className="bg-[#0D1B2A] text-white relative"
        style={{
          backgroundImage:
            "linear-gradient(rgba(13,27,42,0.95) 0%, rgba(13,27,42,0.97) 100%), url('https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=1920&q=60')",
          backgroundSize: "cover",
          backgroundPosition: "center right",
        }}
      >
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-28">
          <div className="max-w-4xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">The LifeIQ Philosophy</div>
            <h1 className="mt-5 font-serif-d text-[42px] sm:text-6xl lg:text-[64px] leading-[1.05] tracking-tight text-white">
              A real-world intelligence and decision-readiness program.
            </h1>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.7] max-w-3xl">
              Most of life's biggest losses come not from a lack of effort or intelligence — but from a gap in understanding. LifeIQ exists to close that gap.
            </p>
          </div>
        </div>
      </section>

      {/* THE HIDDEN GAP */}
      <section className="bg-white">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>The hidden gap</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Two people, the same situation, completely different outcomes.
            </h2>
          </div>
          <p className="mt-10 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            Every day, people make decisions about money, health, careers, technology, government systems, property, and relationships. Yet two people facing the exact same situation often walk away with completely different results. One pays ₹5,000; another pays ₹50,000 for the same thing. One avoids a costly mistake; another lives with it for years. One spots an opportunity; another misses it entirely.
          </p>
          <div className="mt-14 max-w-3xl mx-auto text-center reveal">
            <p className="font-serif-d italic text-[26px] sm:text-[34px] leading-[1.25] text-[#F57F17]">
              "The difference is rarely intelligence. The difference is understanding."
            </p>
          </div>
        </div>
      </section>

      {/* INFORMATION ASYMMETRY */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>The real problem</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              The real problem is information asymmetry.
            </h2>
          </div>

          <p className="mt-10 max-w-3xl text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            In the real world, information is not shared equally. Some people understand systems better, see risks earlier, and grasp consequences before they happen. Whenever there is a large gap between what one person understands and what another does, that gap quietly creates vulnerability.
          </p>

          <div className="mt-12 reveal">
            <div className="text-[15px] sm:text-[16px] font-semibold mb-5" style={{ color: ACCENT }}>That vulnerability shows up as:</div>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
              {VULNERABILITIES.map((v, i) => (
                <li key={i} data-testid={`vuln-${i}`} className="flex items-start gap-3 text-[16px] sm:text-[17px] text-[#1A1A1A] leading-[1.6]">
                  <span className="mt-[10px] w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: ACCENT }} />
                  <span>{v}</span>
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-12 max-w-3xl font-serif-d text-[22px] sm:text-[26px] leading-[1.35] reveal" style={{ color: ACCENT }}>
            The problem is rarely dishonesty. Far more often, it is simply a lack of understanding — on one side of the table.
          </p>
        </div>
      </section>

      {/* COST OF NOT KNOWING */}
      <section className="bg-white">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>What the gap costs</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              What the gap actually costs.
            </h2>
          </div>
          <p className="mt-10 text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            A mechanic recommends a repair that wasn't needed. A broker charges more than the work was worth. A salesperson sells a product that doesn't fit. An advisor suggests something unsuitable. A student picks a career on incomplete information. A family spends years on a problem that basic awareness would have prevented. In every case, the cost is created by the same thing — a gap between reality and understanding. And the wider the gap, the larger the loss.
          </p>
          <div className="mt-14 max-w-3xl mx-auto text-center reveal">
            <p className="font-serif-d italic text-[26px] sm:text-[34px] leading-[1.25] text-[#F57F17]">
              "The larger the gap between reality and understanding, the larger the potential loss."
            </p>
          </div>
        </div>
      </section>

      {/* WHY TRADITIONAL EDUCATION ISN'T ENOUGH */}
      <section className="bg-[#F5F5F4]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>Why the syllabus isn't enough</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Schools teach subjects. Life asks different questions.
            </h2>
          </div>

          <p className="mt-10 max-w-3xl text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            Mathematics, science, languages, examinations — these matter, and school teaches them well. But adult life quietly asks a different set of questions that no syllabus covers:
          </p>

          <ul className="mt-10 space-y-4 max-w-3xl reveal">
            {QUESTIONS.map((q, i) => (
              <li
                key={i}
                data-testid={`q-${i}`}
                className="font-serif-d text-[20px] sm:text-[22px] text-[#0D1B2A] leading-[1.4] border-l-[3px] pl-5"
                style={{ borderColor: ACCENT, transitionDelay: `${(i % 4) * 50}ms` }}
              >
                {q}
              </li>
            ))}
          </ul>

          <p className="mt-12 max-w-3xl text-[17px] sm:text-[19px] text-[#1A1A1A] leading-[1.8] reveal">
            Most students reach adulthood having never been taught these — and so they remain dependent on others for decisions that shape their own lives.
          </p>
        </div>
      </section>

      {/* THE MISSION */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">Our mission</div>
            <h2 className="mt-5 font-serif-d text-[36px] sm:text-5xl lg:text-[54px] leading-[1.06] text-white">
              Our mission.
            </h2>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.75]">
              LifeIQ exists to reduce the gap between the complexity of the real world and a young person's ability to navigate it with confidence. We cannot remove every risk, every challenge, or every mistake. But we can equip students with the awareness, knowledge, and decision intelligence to reduce unnecessary losses, avoid preventable mistakes, and make genuinely informed choices.
            </p>
          </div>
        </div>
      </section>

      {/* THREE LEVELS */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <div className="max-w-3xl reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: ACCENT }}>How we close the gap</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[52px] leading-[1.08]">
              Three levels. One purpose: closing the gap, step by step.
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {LEVELS.map((l, i) => (
              <Link
                key={i}
                to={l.href}
                data-testid={`phil-level-${i}`}
                className="reveal bg-white border border-[#E5E7EA] rounded-sm overflow-hidden flex flex-col hover:shadow-[0_8px_30px_rgba(13,27,42,0.07)] hover:-translate-y-0.5 transition group"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div className="h-1.5" style={{ backgroundColor: l.accent }} />
                <div className="p-7 sm:p-8 flex flex-col gap-4 grow">
                  <div className="text-[12px] uppercase tracking-[0.22em] font-semibold" style={{ color: l.accent }}>{l.badge}</div>
                  <p className="text-[16px] text-[#1A1A1A] leading-[1.7]">{l.body}</p>
                  <div className="mt-auto pt-2 text-[12px] uppercase tracking-[0.18em] font-semibold flex items-center gap-2" style={{ color: l.accent }}>
                    Explore this level
                    <span className="transition-transform group-hover:translate-x-0.5">→</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* THE GOAL */}
      <section className="bg-[#0D1B2A] text-white">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32 text-center">
          <div className="reveal">
            <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">What we are really building</div>
            <h2 className="mt-5 font-serif-d text-[36px] sm:text-5xl lg:text-[54px] leading-[1.06] text-white max-w-3xl mx-auto">
              What we are really building.
            </h2>
            <p className="mt-8 text-[17px] sm:text-[19px] text-white/85 leading-[1.75] max-w-2xl mx-auto">
              Individuals who are not easily confused, manipulated, misled, overcharged, or left dependent and unprepared. People who understand enough about the world to make confident, responsible, and informed decisions.
            </p>
            <div className="mt-14 max-w-3xl mx-auto reveal">
              <p className="font-serif-d italic text-[26px] sm:text-[34px] leading-[1.25] text-[#F57F17]">
                "The biggest losses in life rarely come from a lack of effort — but from a lack of understanding. Education should exist to close that gap."
              </p>
            </div>
            <Link to="/the-program" data-testid="phil-cta" className="mt-12 inline-flex items-center gap-2 bg-[#F57F17] text-white px-8 py-4 text-[13px] font-semibold tracking-wide uppercase hover:brightness-95 transition rounded-sm">
              See How It Works <ArrowRight size={15} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
